import { Locales } from '@griply/types';
import { ErrorLevel } from '@griply/utils';
import {
  getAdditionalUserInfo,
  OAuthProvider,
  signInWithCredential,
} from 'firebase/auth';
import i18n from 'i18next';
import { jwtDecode } from 'jwt-decode';
import { getErrorReporter } from 'shared/services/error-tracking';
import {
  finishGoogleSignIn,
  getSearchParamGoogleIdToken,
  hasMatchingSignInNonce,
  removeGoogleIdTokenFromUrl,
} from 'shared/services/google';
import {
  clearOnboardingAnswers,
  getNonce,
} from 'shared/services/local-storage';
import {
  getSearchParamIdToken,
  removeIdTokenFromUrl,
} from 'shared/utils/get-search-param-id-token';

import { finishUserSignup } from './finish-user-signup';
import { getUser } from './get-user';
import { getAuthentication } from './helpers';
import { channelLogin } from './helpers/auth';
import { processOnboardingAnswers } from './process-onboarding-answers';
import { setPushTokenOnUser } from './set-push-token-on-user';
import { setUserCountry } from './set-user-country';

type AppleJwt = {
  iss?: string;
  user?: { firstName: string; lastName: string };
  state?: string;
};

const signInWithApple = async (token: string) => {
  // get the nonce used for sign-in
  const nonce = getNonce();
  const rawNonce = nonce?.raw;

  try {
    const decryptedToken = jwtDecode<AppleJwt>(token);
    const { user, state } = decryptedToken;

    const auth = await getAuthentication();

    const provider = new OAuthProvider('apple.com');
    const credential = provider.credential({
      idToken: token,
      rawNonce,
    });

    const result = await signInWithCredential(auth, credential);
    const isSignup = getAdditionalUserInfo(result)?.isNewUser ?? false;

    // if signup, finish signup
    if (isSignup) {
      await finishUserSignup({
        uid: auth.currentUser?.uid,
        name: user ? `${user?.firstName} ${user.lastName}` : undefined,
        locale: (i18n.resolvedLanguage ?? i18n.language) as Locales,
        queryParameters: state ? JSON.parse(state) : undefined,
      });

      // Process onboarding answers after signup
      await processOnboardingAnswers();
      clearOnboardingAnswers();
    }
  } catch (error) {
    getErrorReporter().captureException(error, {
      tags: { source: 'sign-in-with-apple' },
      extra: {
        nonce: nonce?.hashed,
        hasRawNonce: !!rawNonce,
        hasToken: !!token,
      },
    });
  }
};

export const signInWithToken = async () => {
  const googleIdToken = getSearchParamGoogleIdToken();
  const appleIdToken = getSearchParamIdToken();

  const idToken = googleIdToken ?? appleIdToken;

  if (!idToken) {
    return;
  }

  // Nothing else ties the token in the URL to a sign-in this browser started — both providers echo the hashed nonce back.
  if (!hasMatchingSignInNonce(idToken)) {
    removeGoogleIdTokenFromUrl();
    removeIdTokenFromUrl();
    getErrorReporter().captureMessage(
      'third-party sign-in rejected: token carries no matching nonce',
      ErrorLevel.Error,
      {
        tags: { source: 'third-party-sign-in-nonce' },
        extra: {
          hasStoredNonce: !!getNonce(),
          provider: googleIdToken ? 'google' : 'apple',
        },
        fingerprint: ['third-party-sign-in-nonce'],
      },
    );
    return;
  }

  const auth = await getAuthentication();
  // Runs only once the token is known to belong to this browser, so a crafted link cannot sign anyone out.
  await auth.signOut();

  if (googleIdToken) {
    try {
      await finishGoogleSignIn(googleIdToken);
    } catch (error) {
      getErrorReporter().captureException(error, {
        tags: { source: 'finish-google-sign-in' },
      });
    }
  } else if (appleIdToken) {
    try {
      await signInWithApple(appleIdToken);
    } catch (error) {
      getErrorReporter().captureException(error, {
        tags: { source: 'sign-in-with-apple-wrapper' },
      });
    }
  }

  try {
    const user = await getUser();

    if (!user) {
      return;
    }

    await setPushTokenOnUser(user.uid, user.settings?.language || Locales.EnUS);
  } catch (error) {
    getErrorReporter().captureException(error, {
      tags: { source: 'set-push-token-after-third-party-sign-in' },
    });
  }

  try {
    await setUserCountry();
  } catch (error) {
    getErrorReporter().captureException(error, {
      tags: { source: 'set-user-country-after-third-party-sign-in' },
    });
  }

  // log other tabs in as well
  channelLogin();
};
